import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import {
  isCnsMember,
  isMnrCosmosMember,
} from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { isEniNiceMember as checkIsEniNiceMember } from '../../../../frontends/GuestExperience/utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import {
  FacilityDetails,
  FacilityLocationsResponse,
} from '../../../../models/FacilityDetails';
import {
  AcoProvider,
  HcbsService,
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../../models/ProviderDetails';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import {
  returnInclusionFlag,
  returnSuppressFlag,
  useSuppressRRVirtualCareOnCEXWidget,
} from '../../../../utils/featureSuppress';
import {
  getInNetworkStartDate,
  getOfferVirtualCareText,
  getPcmhValue,
  getPracticeLimitations,
  getTexasValue,
  getYesNoNAText,
  isDisplayTexasStatesHealthProvider,
  isHospitalsOrAmbulatorySurgicalFacilitites,
  mapLanguagesSpokenByOffice,
  showECP,
  showPcmhAttribute,
  showSpecialtiesForAmbulatorySurgicalFacility,
  showSpecialtiesForHospital,
} from '../../../../utils/providerDetails.utils';
import { capitalizeEachWord } from '../../../../utils/providerNames.utils';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import {
  getCurrentMember,
  getMemberCoverageTypeSitusState,
  isDsnpMember,
} from '../../../../utils/user.utils';
import {
  Constants,
  FacilityCategory,
  ReverseCoverageTypesCodes,
} from '../../../Constants';
import { ConstantsRoutes } from '../../../ConstantsRoutes';
import {
  h2SmallerFontForResponsive,
  mobileOnly,
  phoneOnly,
} from '../../../ConstantsStyles';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import { convertObjectToUrlParams } from '../../../Utils';
import { CopyToClipBoard } from '../CopyToClipBoard';
import { InfoProviderContent } from '../InfoProviderContent';
import {
  IconAlignmentContainer,
  InnerContainer,
  ProgramContainer,
  SectionHeader,
} from '../ProviderLocation.style';
import {
  FlexContainerList,
  infoContainer,
  infoProviderContentContainer,
  infoTitleContainer,
} from '../ProviderLocationsDetails.style';
import { HcbsServiceInfo } from './HcbsServiceInfo';
import { NetworkAffiliation } from './NetworkAffiliationsCard';
import { NetworkAffiliationsSection } from './NetworkAffiliationsSection';
import { PreferredFacilityInfo } from './PreferredFacilityInfo';
import { SpecialtiesOfferedByFacility } from './SpecialtyOfferedByFacility';
import ToggleExpand from './ToggleExpand';

type Props = {
  providerDetails: ProviderDetails | FacilityDetails;
  selectedLocation: ProviderLocation | FacilityLocationsResponse;
};

function showACO(
  acoProvider: AcoProvider[],
  isMnr: boolean,
  isCns: boolean,
  isIfp: boolean
) {
  return acoProvider && !isMnr && !isCns && !isIfp;
}

const bhProgramInfo = (
  t,
  isBehavioralHealth,
  isOrganization,
  showBHprograms,
  infoSub,
  infoTit = ''
) =>
  isBehavioralHealth &&
  isOrganization &&
  showBHprograms &&
  infoSub && (
    <InfoProviderContent
      icon="playlist_add_check"
      infoSubtext={infoSub}
      infoTitle={t(infoTit)}
    />
  );

const shouldShowHcbsAttribute = (
  isOrganization,
  isCns,
  isMnr,
  checkDsnpMember
) => isOrganization && (isCns || (isMnr && checkDsnpMember));

export const AllOtherInfosContainer = ({
  providerDetails,
  selectedLocation,
}: Props) => {
  const uiMessaging = useLagoon('ui-messaging')();

  const [
    showBHprograms,
    showNetworkAffiliation,
    enableAMH,
    enableHCBS,
    enableViewMoreSpecialtiesInFacilityLocationTab,
    isLocationSpecialtiesEnable,
    enableRRVirtualCareCEXWidget,
    enableColorCodedSpecialties,
    enableNetworkAffiliationFlow,
    enableGuestNiceDecIdEnrollmentFlag,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.BH_PROGRAMS_DETAILS,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_NETWORK_AFFILIATION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ADVANCED_HOME_TIERING,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HCBS_FACILITY_LOCATION_TAB,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_VIEW_MORE_SPECIALTIES_IN_FACILITY_LOCATION_TAB,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SPECIALTIES_LOCATION_LEVEL,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RR_VIRTUAL_CARE_CEX_WIDGET,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COLOR_CODED_SPECIALTIES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GUEST_NICE_DEC_ID_ENROLLMENT,
  ]);

  const { t } = useTranslation();
  const { portalData } = useContext(PortalContext);
  const isWidget = useStore(StoreKeys.IS_WIDGET);

  const mobileScreen = useMediaQuery(mobileOnly) || isWidget;
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;

  const hcbsServiceData = selectedLocation.hcbsService as HcbsService;

  const { navigate } = useRouter();
  const isGuestUser = !!portalData?.portalConfig?.isGuestUser;

  const currentMember = getCurrentMember();
  const checkDsnpMember = isDsnpMember(currentMember);
  const {
    lineOfBusiness,
    population,
    policyNumber,
    searchDirectory,
    planCategory,
  } = currentMember;

  const configLagoon = useLagoon('config');
  const eniNicePlanCategoryName = getLagoonConfigValue(
    configLagoon,
    ConstantsLagoon.CONFIG.ENI_NICE_PLAN_CATEGORY_NAME
  );

  const situsState = getMemberCoverageTypeSitusState(currentMember);

  const { isIFPEarlyPay, isIFPMember } = currentMember || {};
  const isMnr = isMnrCosmosMember(lineOfBusiness, population);
  const isCns = isCnsMember(lineOfBusiness);
  const isCnsOhio = returnInclusionFlag(
    ConstantsLagoon.FEATURE_INCLUSION_FLAGS.INCLUDE_CNS_OH
  );
  const includeAMHInfo = returnInclusionFlag(
    ConstantsLagoon.FEATURE_INCLUSION_FLAGS.AMH_VISIBILITY_STATES,
    false
  );
  const shouldShowAMHInfo =
    enableAMH &&
    situsState === selectedLocation?.address?.state &&
    includeAMHInfo;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType } = chipStore;

  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressRRVirtualCareOnCEXWidget = useSuppressRRVirtualCareOnCEXWidget(
    coverageType,
    policyNumber
  );

  const suppressVirtualCare =
    isVirtualCareQuickCareSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed) ||
    (enableRRVirtualCareCEXWidget && suppressRRVirtualCareOnCEXWidget);

  const networkAffiliationPopoverContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.NETWORK_AFFILIATION_POPOVER_CONTENT,
  })?.message;

  const patientCenterPopoverContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING
      .PATIENT_CENTER_MEDICAL_HOMES_POPOVER_CONTENT,
  });

  const texasHealthStepsProvider = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.TEXAS_HEALTH_STEPS_PROVIDER,
  });

  const essentialCommunityProviderContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.ESSENTIAL_COMMUNITY_PROVIDER_CONTENT,
  });

  const platinumRecognitionPopoverContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PLATINUM_RECOGNITION_POPOVER_CONTENT,
  });

  const { amhTierType, amhId } = selectedLocation as ProviderLocation;
  const specialtiesOfferedByFacilityTitle = find(uiMessaging, {
    key: ConstantsLagoon.FACILITY_LOCATIONS.SPECIALTIES_OFFERED_BY_FACILITY,
  });

  const advancedMedicalHomeContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING[
      `ADVANCED_MEDICAL_HOME_INFO_ICON_BANNER_TIER_${
        amhTierType?.match(/\d+/)?.[0]
      }`
    ],
  });

  const { choosePCP: isChoosePCPFlow = false } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );

  const allConfigs = useLagoon('config')();
  const hospitalsOrgCodes = allConfigs?.find(
    (row) => row.key === 'HOSPITAL_ORG_CODES'
  )?.value;
  const ambulatorySurcialOrgCodes = allConfigs?.find(
    (row) => row.key === 'AMBULATORY_SURGICAL_ORG_CODE'
  )?.value;

  const facilityAttributes = useLagoon('facility-attributes')();
  const additionalAttributes = useLagoon('additional-attributes')();

  const {
    providerId = '',
    providerType,
    organizationCode,
    specialties,
  } = providerDetails;

  const category = facilityAttributes?.find(
    (row) => row?.facilitySubCategoryCode === organizationCode?.[0]
  );

  const {
    accessibility,
    facilityProviderSpecialties,
    hasEveningAppointments,
    hasPCPServiceAvailability,
    isExpressAccessProvider,
    languagesSpokenByOffice,
    languagesSpokenByInterpreters,
    networkAffiliations,
    medicalEquipments,
    network,
    pcpId,
    practiceLimitations,
    providerGroupsAffiliations,
    virtualCareOffered,
    acoProvider,
    isTexasHealthStepProvider,
    isPCMH,
    isECP,
  } = selectedLocation;

  const networks =
    'networks' in selectedLocation ? selectedLocation.networks : undefined;

  const flatNetworkList =
    networks?.flatMap((network) => network.networkAffiliations || []) || [];
  const allNetworkAffiliations = flatNetworkList.filter(
    (affiliation) =>
      affiliation.networkAffiliationId !== null &&
      affiliation.networkAffiliationName !== null &&
      (!isChoosePCPFlow ||
        (!!affiliation.acceptingNewPatients &&
          String(affiliation.pcpIndicator).toLowerCase() === 'true'))
  );

  const isEniNiceGuestMember = checkIsEniNiceMember(
    lineOfBusiness,
    searchDirectory,
    planCategory,
    eniNicePlanCategoryName
  );

  // Entire "hide Provider ID for guests" solution is gated behind the
  // ENABLE_GUEST_NICE_DEC_ID_ENROLLMENT feature flag and only applies to
  // ENI NICE guest members, to allow release control.
  const shouldHideProviderIdForGuest =
    isGuestUser &&
    enableGuestNiceDecIdEnrollmentFlag &&
    isEniNiceGuestMember &&
    flatNetworkList.filter(
      (affiliation) =>
        affiliation.networkAffiliationId !== null &&
        affiliation.networkAffiliationName !== null &&
        !!affiliation.acceptingNewPatients &&
        String(affiliation.pcpIndicator).toLowerCase() === 'true'
    ).length > 0;

  const {
    hasAnesthesiology,
    hasEmergencyMedicine,
    hasPathology,
    hasRadiology,
  } = facilityProviderSpecialties || {};

  const isPractitioner = providerType === ProviderType.PRACTITIONER;
  const isOrganization = providerType === ProviderType.ORGANIZATION;
  const isMedicalGroup = providerType === ProviderType.MEDICAL_GROUP;
  const isProviderGroup = providerType === ProviderType.PROVIDER_GROUP;
  const isBehavioralHealth =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];
  const isMedicalCoverage =
    coverageType === ReverseCoverageTypesCodes['MEDICAL'];

  // For CNS/IFP members, restrict to medical coverage only.
  const isEligibleForNA = isCns || isIFPMember ? isMedicalCoverage : true;
  const isMedicalProvider = isPractitioner && isMedicalCoverage;
  const isHospital = isHospitalsOrAmbulatorySurgicalFacilitites(
    organizationCode,
    hospitalsOrgCodes
  );
  const isAmbulatorySurgicalFacility =
    isHospitalsOrAmbulatorySurgicalFacilitites(
      organizationCode,
      ambulatorySurcialOrgCodes
    );

  const showACOLabel = showACO(
    selectedLocation?.acoProvider,
    isMnr,
    isCns,
    isIFPMember
  );
  const inNetworkStartDate = getInNetworkStartDate(network?.start);
  const isTexasValidPolicyId: boolean = isDisplayTexasStatesHealthProvider(
    additionalAttributes,
    coverageType,
    policyNumber,
    providerType,
    isCns
  );

  const practiceLimitation = getPracticeLimitations(
    practiceLimitations?.age,
    practiceLimitations?.gender
  );

  const isIFPOrganization = isOrganization && isIFPMember;

  const showHcbsAttribute = shouldShowHcbsAttribute(
    isOrganization,
    isCns,
    isMnr,
    checkDsnpMember
  );

  const handleRedirectToProviderGroupDetails = (providerGroupClicked) => {
    const typeaheadStoreValues = {
      sectionType: Constants.RESULT_SECTION.PROVIDER_GROUPS,
    };
    const detailsStoreValues = {
      providerId: providerGroupClicked.id,
    };
    setTypeaheadSearchStore(typeaheadStoreValues);
    setDetailsStore(detailsStoreValues);

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_ID,
      providerGroupClicked.id
    );
    const urlParams = convertObjectToUrlParams(chipStore, {
      ...detailsStoreValues,
      ...typeaheadStoreValues,
    });
    navigate(`${ConstantsRoutes.PROVIDER_GROUP_DETAILS.path}${urlParams}`);
  };

  const handleSearchAllProviders = (affiliation: NetworkAffiliation) => {
    const affiliationName = capitalizeEachWord(
      affiliation.networkAffiliationName?.toLowerCase()
    );

    // Determine sectionType based on provider type to limit results
    const sectionType = isOrganization
      ? Constants.RESULT_SECTION.FACILITY
      : isProviderGroup || isMedicalGroup
      ? Constants.RESULT_SECTION.PROVIDER_GROUPS
      : Constants.RESULT_SECTION.PROVIDER;

    const typeaheadStoreValues = {
      search: affiliationName,
      searchType: Constants.SEARCH_TYPES.SPECIALTY,
      sectionType,
      coverageType,
      isUserTextSearch: true,
      isTypeaheadStoreValuesSet: true,
      networkAffiliationId: [affiliation.networkAffiliationId],
      includeSpecialityRollupCodes: '',
    };

    setTypeaheadSearchStore(typeaheadStoreValues, { replace: true });
    setSearchStore({ toggleView: 'list' });

    // Store the networkAffiliationId so the search query can pick it up
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.NETWORK_AFFILIATION_SEARCH_ID,
      [affiliation.networkAffiliationId]
    );

    // Set a click timestamp so ResultFilter can distinguish active clicks from back navigation
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.NETWORK_AFFILIATION_CLICK_TIMESTAMP,
      Date.now().toString()
    );

    const urlParams = convertObjectToUrlParams({} as ChipState, {
      search: affiliationName,
      searchType: Constants.SEARCH_TYPES.SPECIALTY,
      sectionType,
      coverageType,
      fromLandingPage: true,
      active: true,
      pcpIndicator: false,
      networkAffiliationId: affiliation.networkAffiliationId,
      networkAffiliationName: affiliationName,
    });
    navigate(
      `${ConstantsRoutes.PROVIDER_SEARCH_RESULTS_VIEW_ALL.path}${urlParams}`
    );
  };

  const useFacilityProgramRes = (programTitle) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const titles =
      selectedLocation?.facilityProgramIds
        ?.filter(
          (facilityProgram: any) => facilityProgram?.category === programTitle
        )
        .map((facilityProgram: any) => facilityProgram?.title) || [];

    if (!titles || titles.length === 0) {
      return null;
    }
    const displayedTitles = isExpanded ? titles : titles.slice(0, 3);
    const handleToggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    return (
      <div>
        <div style={{ padding: '6px 16px' }}>
          <ul style={{ listStyleType: 'disc' }}>
            {displayedTitles.map((title, index) => (
              <li
                data-testid={`${programTitle
                  .replaceAll(' ', '-')
                  .toLowerCase()}-sub-headings`}
                key={index}
              >
                {title}
              </li>
            ))}
          </ul>
        </div>
        {titles.length > 3 && (
          <ToggleExpand
            handleToggleExpand={handleToggleExpand}
            isExpanded={isExpanded}
            length={titles.length - 3}
            programTitle={programTitle}
          />
        )}
      </div>
    );
  };

  const usePlatinumProgramsRes = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const allTitles =
      selectedLocation?.platinumPrograms?.flatMap(
        (platProgram: any) => platProgram?.titles
      ) || [];

    const formattedTitles = allTitles;

    if (!allTitles || allTitles.length === 0) {
      return null;
    }
    const displayedTitles = isExpanded
      ? formattedTitles
      : formattedTitles.slice(0, 3);
    const handleToggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    return (
      <div>
        <div style={{ padding: '6px 16px' }}>
          <ul style={{ listStyleType: 'disc' }}>
            {displayedTitles.map((title, index) => (
              <li key={index}>{title}</li>
            ))}
          </ul>
        </div>
        {formattedTitles.length > 3 && (
          <ToggleExpand
            handleToggleExpand={handleToggleExpand}
            isExpanded={isExpanded}
            length={allTitles.length - 3}
            programTitle="platinum recognition"
          />
        )}
      </div>
    );
  };

  const {
    providerLocations: notAvailableOrUnknownText,
    headingStyle,
    descriptionStyle,
    isNotAvailableUnknownTextEnabled,
  } = useNotAvailableUnknownText();

  const fontSize = isNotAvailableUnknownTextEnabled ? '16px' : '18px';
  const resolvedFacilityId = pcpId?.replace(' ', '') || providerId || '';

  const platinumProgramRes = usePlatinumProgramsRes();

  const platinumProgram = bhProgramInfo(
    t,
    isBehavioralHealth,
    isOrganization,
    showBHprograms,
    platinumProgramRes
  );
  const mentalHealthProgram = bhProgramInfo(
    t,
    isBehavioralHealth,
    isOrganization,
    showBHprograms,
    useFacilityProgramRes('Mental Health Program'),
    'Mental health programs'
  );
  const substanceAbuse = bhProgramInfo(
    t,
    isBehavioralHealth,
    isOrganization,
    showBHprograms,
    useFacilityProgramRes('Substance Abuse Program'),
    'Substance use programs'
  );
  const eatingDisorderProgram = bhProgramInfo(
    t,
    isBehavioralHealth,
    isOrganization,
    showBHprograms,
    useFacilityProgramRes('Eating Disorder'),
    'Eating disorder programs'
  );

  const isPlatinumProgramSectionActive =
    platinumProgram ||
    mentalHealthProgram ||
    substanceAbuse ||
    eatingDisorderProgram;

  const SpecialtiesOfferedByFacilityModalContent = find(uiMessaging, {
    key: ConstantsLagoon.CONTENT_FILTERS.SPECIALTY_OFFERED_TOOLTIP_INFO_ICON,
  });

  return (
    <React.Fragment>
      <ProgramContainer
        active={!!isPlatinumProgramSectionActive}
        cssProps={isPlatinumProgramSectionActive}
      >
        {(platinumProgram ||
          mentalHealthProgram ||
          substanceAbuse ||
          eatingDisorderProgram) && (
          <Flex css={infoProviderContentContainer(mobileScreen)}>
            <SectionHeader justify="flex-end">
              <Heading
                css={h2SmallerFontForResponsive}
                data-auto-testid="programs-title"
                data-testid="programs-title"
                level={2}
                size="sm"
              >
                {t('PROVIDER_DETAILS.LOCATIONS_TAB.PROGRAMS')}
              </Heading>
            </SectionHeader>
          </Flex>
        )}

        <Flex
          css={infoProviderContentContainer(mobileScreen)}
          data-testid="all-other-infos-container"
        >
          {!isCns && !isIFPMember && isOrganization && (
            <PreferredFacilityInfo
              detailType={providerType}
              selectedLocation={selectedLocation}
            />
          )}

          {bhProgramInfo(
            t,
            isBehavioralHealth,
            isOrganization,
            showBHprograms,
            platinumProgramRes
          ) && (
            <InfoProviderContent
              icon="star"
              infoSubtext={platinumProgramRes}
              infoTitle={t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
              popoverContent={platinumRecognitionPopoverContent?.message}
              popoverTitle={platinumRecognitionPopoverContent?.title}
              showModal
            />
          )}

          {bhProgramInfo(
            t,
            isBehavioralHealth,
            isOrganization,
            showBHprograms,
            useFacilityProgramRes('Mental Health Program'),
            'Mental health programs'
          )}
          {bhProgramInfo(
            t,
            isBehavioralHealth,
            isOrganization,
            showBHprograms,
            useFacilityProgramRes('Substance Abuse Program'),
            'Substance use programs'
          )}
          {bhProgramInfo(
            t,
            isBehavioralHealth,
            isOrganization,
            showBHprograms,
            useFacilityProgramRes('Eating Disorder'),
            'Eating disorder programs'
          )}
        </Flex>
      </ProgramContainer>

      <InnerContainer>
        <SectionHeader justify="flex-end">
          <Heading
            css={h2SmallerFontForResponsive}
            data-auto-testid="services-title"
            data-testid="services-title"
            level={3}
            size="sm"
            style={
              {
                marginBottom: '-10px',
              } as React.CSSProperties
            }
          >
            {t('PROVIDER_DETAILS.LOCATIONS_TAB.SERVICES')}
          </Heading>
        </SectionHeader>
        <Flex
          as="ul"
          css={infoProviderContentContainer(mobileScreen)}
          data-testid="all-other-infos-container-2"
        >
          {!suppressVirtualCare && !isIFPOrganization && (
            <InfoProviderContent
              data-auto-testid="videocam-icon"
              icon="videocam"
              infoSubtext={getOfferVirtualCareText(
                virtualCareOffered?.join(', ')
              )}
              infoTitle={t('PROVIDER_LOCATIONS_DETAILS.OFFERS_VIRTUAL_CARE')}
            />
          )}
          <InfoProviderContent
            icon="date_range"
            infoSubtext={getYesNoNAText(t, hasEveningAppointments)}
            infoTitle={t(
              'PROVIDER_LOCATIONS_DETAILS.OFFERS_EVENING_APPOINTMENT'
            )}
          />

          <InfoProviderContent
            icon="translate"
            infoSubtext={mapLanguagesSpokenByOffice(languagesSpokenByOffice, t)}
            infoTitle={t(
              'PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_OFFICE'
            )}
          />
          {showACOLabel && (
            <InfoProviderContent
              icon="corporate_fare"
              infoSubtext={
                capitalizeEachWord(acoProvider[0]?.acoNetworkDescription) ||
                t('PROVIDER_LOCATIONS_DETAILS.NOT_APPLICABLE')
              }
              infoTitle={t(
                'PROVIDER_LOCATIONS_DETAILS.ACCOUNTABLE_CARE_ORGANIZATION'
              )}
            />
          )}
          <InfoProviderContent
            icon="translate"
            infoSubtext={languagesSpokenByInterpreters
              ?.sort((a, b) => a.localeCompare(b))
              .join(', ')}
            infoTitle={t(
              'PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_INTERPRETER'
            )}
          />

          <InfoProviderContent
            icon="accessibility"
            infoSubtext={accessibility?.join(', ')}
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.ACCESSIBILITY')}
          />
          {!isBehavioralHealth && (
            <InfoProviderContent
              icon="local_hospital"
              infoSubtext={getYesNoNAText(t, hasPCPServiceAvailability)}
              infoTitle={t('PROVIDER_LOCATIONS_DETAILS.PCP_SERVICES_AVAILABLE')}
              variant="outlined"
            />
          )}
          {isLocationSpecialtiesEnable && specialties?.length > 0 && (
            <InfoProviderContent
              icon="inventory"
              infoTitle={t('PROVIDER_LOCATIONS_DETAILS.SPECIALTIES')}
              variant="outlined"
            >
              <ul>
                {specialties.map((specialty, index) => (
                  <li key={index} style={{ color: '#323333' }}>
                    {specialty}
                  </li>
                ))}
              </ul>
            </InfoProviderContent>
          )}
          {isCnsOhio && (
            <React.Fragment>
              <InfoProviderContent
                icon="model_training"
                infoSubtext={getYesNoNAText(t, undefined)}
                infoTitle={t(
                  'PROVIDER_LOCATIONS_DETAILS.SPECIALIZED_STAFF_TRAINING'
                )}
              />
              <InfoProviderContent
                icon="connect_without_contact"
                infoSubtext={getYesNoNAText(t, undefined)}
                infoTitle={t(
                  'PROVIDER_LOCATIONS_DETAILS.STAFF_CULTURAL_COMPETENCE'
                )}
              />
            </React.Fragment>
          )}

          {isMnr && showNetworkAffiliation && (
            <InfoProviderContent
              icon="corporate_fare"
              infoSubtext={networkAffiliations?.join(', ')}
              infoTitle={t('PROVIDER_LOCATIONS_DETAILS.NETWORK_AFFILIATION')}
              popoverContent={networkAffiliationPopoverContent}
              popoverTitle={
                t('PROVIDER_LOCATIONS_DETAILS.NETWORK_AFFILIATION') || undefined
              }
              showPopover
            />
          )}

          {isPractitioner && (
            <FlexContainerList
              css={{
                marginBottom: '3px',
                '@media (max-width: 464px)': { marginBottom: '0' },
              }}
            >
              <IconAlignmentContainer>
                <IconSymbol
                  color="$core.color.brand.100"
                  css={{ marginTop: '3px' }}
                  icon="group"
                  size={18}
                />
                <Text
                  css={{ ...infoContainer(phoneScreen), ...headingStyle }}
                  fontWeight="medium"
                >
                  {t('PROVIDER_LOCATIONS_DETAILS.PROVIDER_GROUP')}
                  {providerGroupsAffiliations?.length > 0 ? (
                    providerGroupsAffiliations.map((group) => (
                      <Text
                        css={infoContainer(phoneScreen)}
                        fontWeight="regular"
                        key={`provider-group-${group.value}`}
                      >
                        {isWidget ? (
                          <React.Fragment>
                            {capitalizeEachWord(group.value)}
                          </React.Fragment>
                        ) : (
                          <LinkWithTracking
                            analyticsInfo={{
                              name: group?.value,
                              location: `body:${Constants.PROVIDER_DETAILS.LOCATIONS_TAB.GROUP_AFFILIATION}`,
                            }}
                            css={{
                              ...infoContainer(phoneScreen),
                              fontSize,
                            }}
                            data-auto-testid="redirect-to-provider-group-details"
                            data-testid="redirect-to-provider-group-details-provider-locations-tab"
                            fontWeight="bold"
                            isDisabled={isIFPEarlyPay || group?.id === null}
                            isStandardAnchor
                            onClick={() =>
                              handleRedirectToProviderGroupDetails(group)
                            }
                          >
                            {capitalizeEachWord(group.value)}
                          </LinkWithTracking>
                        )}
                      </Text>
                    ))
                  ) : (
                    <Text
                      css={{
                        ...infoContainer(phoneScreen),
                        ...descriptionStyle,
                      }}
                      data-testid="provider-group-not-available"
                      fontWeight="regular"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </Text>
              </IconAlignmentContainer>
            </FlexContainerList>
          )}

          {isTexasValidPolicyId && (
            <InfoProviderContent
              icon="group"
              infoSubtext={getTexasValue(isTexasHealthStepProvider)}
              infoTitle={t(
                'PROVIDER_LOCATIONS_DETAILS.TEXAS_HEALTH_STEPS_PROVIDER'
              )}
              popoverContent={texasHealthStepsProvider?.message}
              popoverTitle={texasHealthStepsProvider?.title}
              showModal
            />
          )}
          <InfoProviderContent
            icon="pan_tool"
            infoSubtext={
              practiceLimitation?.trim()?.length > 0
                ? practiceLimitation
                : notAvailableOrUnknownText
            }
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.PRACTICE_LIMITATIONS')}
          />
          {isOrganization &&
            category?.DNT_category?.toLowerCase() ===
              FacilityCategory.DURABLE_MEDICAL_EQUIPMENT.toLowerCase() && (
              <React.Fragment>
                <InfoProviderContent
                  icon="accessible"
                  infoSubtext={medicalEquipments?.join(', ')}
                  infoTitle={t(
                    'PROVIDER_LOCATIONS_DETAILS.MEDICAL_EQUIPMENT_OFFER'
                  )}
                />
              </React.Fragment>
            )}
          {showPcmhAttribute(
            lineOfBusiness,
            isMedicalGroup,
            isProviderGroup,
            isPractitioner
          ) && (
            <InfoProviderContent
              icon="home"
              infoSubtext={getPcmhValue(isPCMH)}
              infoTitle={t(
                'PROVIDER_LOCATIONS_DETAILS.PATIENT_CENTER_MEDICAL_HOME'
              )}
              popoverContent={patientCenterPopoverContent?.message}
              popoverTitle={patientCenterPopoverContent?.title}
              showModal
            />
          )}
          {showECP(lineOfBusiness, situsState) && (
            <InfoProviderContent
              icon="group"
              infoSubtext={getYesNoNAText(t, isECP)}
              infoTitle={t(
                'PROVIDER_LOCATIONS_DETAILS.ESSENTIAL_COMMUNITY_PROVIDER'
              )}
              popoverContent={essentialCommunityProviderContent?.message}
              popoverTitle={essentialCommunityProviderContent?.title}
              showModal
            />
          )}
          <InfoProviderContent
            icon="event"
            infoSubtext={inNetworkStartDate}
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.IN_NETWORK')}
            variant="outlined"
          />
          {isPractitioner &&
            !isBehavioralHealth &&
            !shouldHideProviderIdForGuest && (
              <InfoProviderContent
                icon="id_card"
                infoSubtext={pcpId}
                infoTitle={t('PROVIDER_LOCATIONS_DETAILS.PROVIDER_ID')}
                variant="outlined"
              >
                <Flex
                  css={{
                    gap: '$xs',
                    '@media (max-width: 464px)': { width: 'auto' },
                  }}
                >
                  <Text
                    color="$info1"
                    css={{
                      ...infoContainer(phoneScreen),
                      fontSize,
                    }}
                    fontWeight="bold"
                  >
                    {pcpId?.replace(' ', '') ?? ''}
                  </Text>

                  <CopyToClipBoard
                    gap="0"
                    isNotAvailableUnknownTextEnabled={
                      isNotAvailableUnknownTextEnabled
                    }
                    label={t(
                      'PROVIDER_LOCATIONS_DETAILS.COPY_PROVIDER_ID_TO_CLIPBOARD'
                    )}
                    text={pcpId?.replace(' ', '') ?? ''}
                    title="provider-id"
                  />
                </Flex>
              </InfoProviderContent>
            )}
          {isOrganization &&
            (showSpecialtiesForHospital(isHospital, category?.DNT_category) ||
              showSpecialtiesForAmbulatorySurgicalFacility(
                isAmbulatorySurgicalFacility,
                enableViewMoreSpecialtiesInFacilityLocationTab
              )) && (
              <InfoProviderContent
                icon="task"
                infoTitle={
                  enableViewMoreSpecialtiesInFacilityLocationTab
                    ? specialtiesOfferedByFacilityTitle.message
                    : t('PROVIDER_DETAILS.LOCATIONS_TAB.SPECIALTY_INNETWORK')
                }
                popoverContent={
                  enableViewMoreSpecialtiesInFacilityLocationTab &&
                  enableColorCodedSpecialties
                    ? SpecialtiesOfferedByFacilityModalContent?.message
                    : undefined
                }
                popoverTestId={
                  enableViewMoreSpecialtiesInFacilityLocationTab &&
                  enableColorCodedSpecialties
                    ? 'specialties-offered-by-facility'
                    : undefined
                }
                popoverTitle={
                  enableViewMoreSpecialtiesInFacilityLocationTab &&
                  enableColorCodedSpecialties
                    ? SpecialtiesOfferedByFacilityModalContent?.title
                    : undefined
                }
                showPopover={
                  enableViewMoreSpecialtiesInFacilityLocationTab &&
                  enableColorCodedSpecialties
                }
                variant="outlined"
              >
                {enableViewMoreSpecialtiesInFacilityLocationTab ? (
                  <SpecialtiesOfferedByFacility
                    facilityProviderSpecialties={facilityProviderSpecialties}
                    notAvailableOrUnknownText={notAvailableOrUnknownText}
                  />
                ) : (
                  <div>
                    <Flex
                      css={{ gap: '4px' }}
                      data-testid="sub-heading-speciality-anesthesiolgy"
                    >
                      <Text css={phoneScreen}>
                        {t('PROVIDER_DETAILS.LOCATIONS_TAB.ANESTHESIOLOGY')} -{' '}
                      </Text>
                      <Text css={infoTitleContainer(phoneScreen)}>
                        {getYesNoNAText(t, hasAnesthesiology) ||
                          notAvailableOrUnknownText}
                      </Text>
                    </Flex>
                    <Flex
                      css={{ gap: '4px' }}
                      data-testid="sub-heading-speciality-emergency-medicine"
                    >
                      <Text css={phoneScreen}>
                        {t('PROVIDER_DETAILS.LOCATIONS_TAB.EMERGENCY_MEDICINE')}{' '}
                        -{' '}
                      </Text>
                      <Text css={infoTitleContainer(phoneScreen)}>
                        {getYesNoNAText(t, hasEmergencyMedicine) ||
                          notAvailableOrUnknownText}
                      </Text>
                    </Flex>
                    <Flex
                      css={{ gap: '4px' }}
                      data-testid="sub-heading-speciality-pathology"
                    >
                      <Text css={phoneScreen}>
                        {t('PROVIDER_DETAILS.LOCATIONS_TAB.PATHOLOGY')} -{' '}
                      </Text>
                      <Text css={infoTitleContainer(phoneScreen)}>
                        {getYesNoNAText(t, hasPathology) ||
                          notAvailableOrUnknownText}
                      </Text>
                    </Flex>
                    <Flex
                      css={{ gap: '4px' }}
                      data-testid="sub-heading-speciality-radiology"
                    >
                      <Text css={phoneScreen}>
                        {t('PROVIDER_DETAILS.LOCATIONS_TAB.RADIOLOGY')} -{' '}
                      </Text>
                      <Text css={infoTitleContainer(phoneScreen)}>
                        {getYesNoNAText(t, hasRadiology) ||
                          notAvailableOrUnknownText}
                      </Text>
                    </Flex>
                  </div>
                )}
              </InfoProviderContent>
            )}

          {!isCns && isBehavioralHealth && (
            <InfoProviderContent
              icon="speed"
              infoSubtext={getYesNoNAText(t, isExpressAccessProvider)}
              infoTitle={t(
                'PROVIDER_LOCATIONS_DETAILS.EXPRESS_ACCESS_PROVIDER'
              )}
            />
          )}
          {enableHCBS && showHcbsAttribute && (
            <HcbsServiceInfo
              hcbsServiceData={hcbsServiceData}
              notAvailableOrUnknownText={notAvailableOrUnknownText}
            />
          )}

          {isOrganization && (
            <InfoProviderContent
              icon="location_city"
              infoSubtext={resolvedFacilityId || null}
              infoTitle={t('PROVIDER_LOCATIONS_DETAILS.FACILITY_ID')}
              variant="outlined"
            >
              <Flex
                css={{
                  gap: '$xs',
                  '@media (max-width: 464px)': { width: 'auto' },
                }}
              >
                <Text
                  color="$core.color.brand.60"
                  css={{
                    ...infoContainer(phoneScreen),
                    fontSize,
                  }}
                  fontWeight="bold"
                >
                  {resolvedFacilityId}
                </Text>
                <CopyToClipBoard
                  gap="0"
                  isNotAvailableUnknownTextEnabled={
                    isNotAvailableUnknownTextEnabled
                  }
                  label={t(
                    'PROVIDER_LOCATIONS_DETAILS.COPY_FACILITY_ID_TO_CLIPBOARD'
                  )}
                  text={resolvedFacilityId}
                  title="facility-id"
                />
              </Flex>
            </InfoProviderContent>
          )}

          {shouldShowAMHInfo && isMedicalProvider && (
            <>
              <InfoProviderContent
                icon="id_card"
                infoSubtext={amhId || null}
                infoTitle={t('PROVIDER_LOCATIONS_DETAILS.AMH_PROVIDER_ID')}
                variant="outlined"
              >
                {amhId && (
                  <Flex
                    css={{
                      gap: '$xs',
                      '@media (max-width: 464px)': { width: 'auto' },
                    }}
                  >
                    <Text
                      color="$core.color.brand.60"
                      css={{
                        ...infoContainer(phoneScreen),
                        fontSize,
                      }}
                      data-testid="AMH-Provider-ID-subtext-provider-locations-tab"
                      fontWeight="bold"
                    >
                      {amhId?.replace(' ', '') ?? ''}
                    </Text>
                    <CopyToClipBoard
                      gap="0"
                      isNotAvailableUnknownTextEnabled={
                        isNotAvailableUnknownTextEnabled
                      }
                      label={t(
                        'PROVIDER_LOCATIONS_DETAILS.COPY_AMH_PROVIDER_ID_TO_CLIPBOARD'
                      )}
                      text={amhId?.replace(' ', '') ?? ''}
                      title="amh-provider-id"
                    />
                  </Flex>
                )}
              </InfoProviderContent>
              <InfoProviderContent
                contentPopoverContent={advancedMedicalHomeContent?.message}
                contentPopoverTitle={advancedMedicalHomeContent?.title}
                icon="local_hospital"
                infoSubtext={amhTierType || null}
                infoTitle={t(
                  'PROVIDER_LOCATIONS_DETAILS.ADVANCED_MEDICAL_HOME'
                )}
                showContentPopover
                variant="outlined"
              />
            </>
          )}
        </Flex>
      </InnerContainer>

      {isEligibleForNA &&
        allNetworkAffiliations &&
        allNetworkAffiliations.length > 0 &&
        enableNetworkAffiliationFlow && (
          <InnerContainer>
            <NetworkAffiliationsSection
              affiliations={allNetworkAffiliations}
              coverageType={coverageType}
              isBehavioralHealth={isBehavioralHealth}
              maxCardsToShow={3}
              maxCardsToShowMobile={3}
              maxCardsToShowTablet={2}
              onSearchAll={handleSearchAllProviders}
              pcpId={pcpId}
              providerType={providerType}
            />
          </InnerContainer>
        )}
    </React.Fragment>
  );
};
